import { createQuery, type QueryClient } from '@tanstack/solid-query'
import { isHTTPError } from 'ky'
import { frostingTokenId } from '~/config'
import { api, getCurrentUserId } from '~/services/api'

export type ActivityTokenBalance = {
  balance: number
  token_type_id: number
}

export const activityTokenBalanceQueryKey = [
  'activity-token-balance',
  { tokenTypeId: frostingTokenId },
] as const

export const useActivityTokenBalance = () => {
  return createQuery(() => ({
    queryKey: activityTokenBalanceQueryKey,
    // Fetch once, then let the app own the cache: stamp credits and debits
    // are mirrored locally via setQueryData, so we don't want background
    // refetches (window focus, observer mount, manual invalidate) racing the
    // animations or pulling in a post-mutation server value.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      const userId = await getCurrentUserId()
      try {
        return await api.service
          .get(
            `user/users/${userId}/activity-tokens/${frostingTokenId}/balance`,
          )
          .json<ActivityTokenBalance>()
      } catch (error) {
        // Backend returns 404 when the user has never received this token type
        // (the balance row only exists once they earn or spend at least one
        // stamp). Treat as 0 balance instead of an error so the topnav pill
        // shows "0" on cold load.
        if (isHTTPError(error) && error.response.status === 404) {
          return { balance: 0, token_type_id: frostingTokenId }
        }
        throw error
      }
    },
  }))
}

export function adjustActivityTokenBalance(
  queryClient: QueryClient,
  delta: number,
) {
  queryClient.setQueryData<ActivityTokenBalance>(
    activityTokenBalanceQueryKey,
    (old) => ({
      token_type_id: old?.token_type_id ?? frostingTokenId,
      balance: Math.max(0, (old?.balance ?? 0) + delta),
    }),
  )
}
